import React, { useEffect, useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Tooltip,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import dayjs from 'dayjs';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Edit, Delete, Key, Refresh, FolderSpecial } from '@mui/icons-material';

const BRAND_ORANGE = '#f05a28';
const BRAND_ORANGE_DARK = '#d84f22';
const BRAND_TEXT = '#242424';

const ManageCustomerAccess = ({ open, onClose }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [search, setSearch] = useState('');
  const [busyAction, setBusyAction] = useState(null);
  const [contactsMap, setContactsMap] = useState({});
  const [contactsLoading, setContactsLoading] = useState(false);
  const [viewMode, setViewMode] = useState('user'); // 'user' | 'company'
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactDialogUser, setContactDialogUser] = useState(null);
  const [contactSelection, setContactSelection] = useState([]);

  // Project assignment state
  const currentYear = new Date().getFullYear();
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [projectDialogUser, setProjectDialogUser] = useState(null);
  const [projectSelection, setProjectSelection] = useState([]);
  const [projectPicklist, setProjectPicklist] = useState([]);
  const [loadingPicklist, setLoadingPicklist] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');
  const [picklistStart, setPicklistStart] = useState(`${currentYear}-01-01`);
  const [picklistEnd, setPicklistEnd] = useState(`${currentYear}-12-31`);

  const functions = getFunctions();
  const listUsersFn = httpsCallable(functions, 'listCustomerUsers');
  const setPasswordFn = httpsCallable(functions, 'setCustomerPassword');
  const updateUsernameFn = httpsCallable(functions, 'updateCustomerUsername');
  const updateContactsFn = httpsCallable(functions, 'updateCustomerContacts');
  const updateProjectsFn = httpsCallable(functions, 'updateCustomerProjects');
  const deleteUserFn = httpsCallable(functions, 'deleteCustomerUser');
  const fetchContactsFn = httpsCallable(functions, 'fetchContacts');
  const listPicklistFn = httpsCallable(functions, 'listRentmanProjectsForPicklist');

  useEffect(() => {
    if (open) {
      loadUsers(true);
      loadContacts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadContacts = async () => {
    const LS_KEY = 'brent_contact_cache_v1';
    try {
      setContactsLoading(true);
      const cachedRaw = typeof window !== 'undefined' ? window.localStorage.getItem(LS_KEY) : null;
      if (cachedRaw) {
        const cached = JSON.parse(cachedRaw);
        if (cached.expiresAt && cached.expiresAt > Date.now()) {
          setContactsMap(cached.map || {});
          setContactsLoading(false);
          return;
        }
      }
      const res = await fetchContactsFn();
      const data = res.data || {};
      const list = data.data || data.contacts || [];
      const map = {};
      list.forEach((c) => {
        if (c && c.id) map[c.id] = c.displayname || c.name || `Contact ${c.id}`;
      });
      setContactsMap(map);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(LS_KEY, JSON.stringify({
          map,
          expiresAt: Date.now() + 30 * 60 * 1000, // 30 min
        }));
      }
    } catch (err) {
      console.error('[ManageCustomerAccess] contacts fetch', err);
    } finally {
      setContactsLoading(false);
    }
  };

  const loadUsers = async (reset = false) => {
    try {
      reset ? setLoading(true) : setLoadingMore(true);
      const res = await listUsersFn({
        pageSize: 50,
        startAfter: reset ? null : nextPageToken,
      });
      const data = res.data || {};
      setUsers(reset ? (data.users || []) : [...users, ...(data.users || [])]);
      setNextPageToken(data.nextPageToken || null);
    } catch (err) {
      console.error('[ManageCustomerAccess] list error', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search) return users;
    const q = search.toLowerCase();
    return users.filter((u) =>
      (u.username || '').toLowerCase().includes(q) ||
      (u.usernames || []).some((n) => (n || '').toLowerCase().includes(q)) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.contactIds || []).some((c) => {
        const name = contactsMap[c];
        return String(c).includes(q) || (name && name.toLowerCase().includes(q));
      })
    );
  }, [search, users, contactsMap]);

  const companyGroups = useMemo(() => {
    if (viewMode !== 'company') return [];
    const groups = {};
    filtered.forEach((u) => {
      const ids = (u.contactIds && u.contactIds.length ? u.contactIds : ['unknown']);
      ids.forEach((id) => {
        const key = String(id);
        if (!groups[key]) groups[key] = {
          contactId: key === 'unknown' ? null : id,
          company: contactsMap[id] || (id === 'unknown' ? 'Unknown company' : `Contact ${id}`),
          users: [],
        };
        groups[key].users.push(u);
      });
    });
    return Object.values(groups).sort((a, b) => (a.company || '').localeCompare(b.company || ''));
  }, [filtered, contactsMap, viewMode]);

  const handleChangePassword = async (user) => {
    const newPw = window.prompt(`Set a new password for ${user.email || user.username}:`);
    if (!newPw) return;
    setBusyAction(user.uid);
    try {
      await setPasswordFn({ uid: user.uid, newPassword: newPw });
    } catch (err) {
      console.error('[ManageCustomerAccess] set password', err);
    } finally {
      setBusyAction(null);
    }
  };

  const handleEditUsername = async (user) => {
    const newUsername = window.prompt('New username', user.username || '');
    if (!newUsername || newUsername === user.username) return;
    setBusyAction(user.uid);
    try {
      await updateUsernameFn({
        uid: user.uid,
        oldUsername: user.username,
        newUsername,
      });
      await loadUsers(true);
    } catch (err) {
      console.error('[ManageCustomerAccess] update username', err);
    } finally {
      setBusyAction(null);
    }
  };

  const handleEditContacts = async (user) => {
    setContactDialogUser(user);
    setContactSelection(user.contactIds || []);
    setContactDialogOpen(true);
  };

  const handleContactDialogSave = async () => {
    if (!contactDialogUser) return;
    setBusyAction(contactDialogUser.uid);
    try {
      await updateContactsFn({
        uid: contactDialogUser.uid,
        username: contactDialogUser.username,
        contactIds: contactSelection,
      });
      await loadUsers(true);
    } catch (err) {
      console.error('[ManageCustomerAccess] update contacts', err);
    } finally {
      setBusyAction(null);
      setContactDialogOpen(false);
      setContactDialogUser(null);
    }
  };

  const handleDelete = async (user) => {
    const confirm = window.confirm(`Delete user ${user.username || user.email}? This removes Auth + mapping.`);
    if (!confirm) return;
    setBusyAction(user.uid);
    try {
      await deleteUserFn({ uid: user.uid });
      await loadUsers(true);
    } catch (err) {
      console.error('[ManageCustomerAccess] delete', err);
    } finally {
      setBusyAction(null);
    }
  };

  // --- Project assignment helpers ---
  const loadPicklist = async (start, end) => {
    const s = start || picklistStart;
    const e = end || picklistEnd;
    setLoadingPicklist(true);
    try {
      const res = await listPicklistFn({
        startDate: dayjs(s).startOf('day').toISOString(),
        endDate: dayjs(e).endOf('day').toISOString(),
      });
      if (res.data.success) {
        setProjectPicklist(
          (res.data.data || []).slice().sort((a, b) => (a.displayname || '').localeCompare(b.displayname || '')),
        );
      }
    } catch (err) {
      console.error('[ManageCustomerAccess] picklist fetch', err);
    } finally {
      setLoadingPicklist(false);
    }
  };

  const handleEditProjects = (user) => {
    setProjectDialogUser(user);
    setProjectSelection(user.allowedProjectIds || []);
    setProjectSearch('');
    setPicklistStart(`${currentYear}-01-01`);
    setPicklistEnd(`${currentYear}-12-31`);
    setProjectDialogOpen(true);
    loadPicklist(`${currentYear}-01-01`, `${currentYear}-12-31`);
  };

  const handleProjectDialogSave = async () => {
    if (!projectDialogUser) return;
    setBusyAction(projectDialogUser.uid);
    try {
      await updateProjectsFn({
        uid: projectDialogUser.uid,
        username: projectDialogUser.username,
        allowedProjectIds: projectSelection,
      });
      await loadUsers(true);
    } catch (err) {
      console.error('[ManageCustomerAccess] update projects', err);
    } finally {
      setBusyAction(null);
      setProjectDialogOpen(false);
      setProjectDialogUser(null);
    }
  };

  const filteredPicklist = useMemo(() => {
    if (!projectSearch) return projectPicklist;
    const q = projectSearch.toLowerCase();
    return projectPicklist.filter((p) => (p.displayname || '').toLowerCase().includes(q));
  }, [projectPicklist, projectSearch]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, color: BRAND_ORANGE }}>
        Manage Access
        <Tooltip title="Reload">
          <IconButton size="small" onClick={() => { loadUsers(true); loadContacts(); }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <TextField
            label="Search username/email/contact"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            sx={{
              '& label.Mui-focused': { color: BRAND_ORANGE },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#d9d9d9' },
                '&:hover fieldset': { borderColor: BRAND_ORANGE },
                '&.Mui-focused fieldset': { borderColor: BRAND_ORANGE_DARK },
              },
            }}
          />
          <ToggleButtonGroup
            size="small"
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
          >
            <ToggleButton value="user">By user</ToggleButton>
            <ToggleButton value="company">By company</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {(loading || contactsLoading) ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} sx={{ color: BRAND_ORANGE }} />
            <Typography>Loading...</Typography>
          </Box>
        ) : viewMode === 'user' ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Companies</TableCell>
                <TableCell>Projects</TableCell>
                <TableCell>UID</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((u) => (
                <TableRow key={`${u.username}-${u.uid}`} sx={{ '& > td': { py: 0.5, px: 1 } }}>
                  <TableCell>
                    {u.username}
                    {u.usernames && u.usernames.length > 1 ? (
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                        Also: {u.usernames.filter((n) => n !== u.username).join(', ')}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell sx={{ position: 'relative' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, alignItems: 'flex-start' }}>
                      {(u.contactIds || []).map((cid) => (
                        <Chip
                          key={`${u.uid}-${cid}`}
                          label={contactsMap[cid] || `Contact ${cid}`}
                          size="small"
                          sx={{ backgroundColor: 'rgba(240,90,40,0.1)', color: BRAND_TEXT, height: 22 }}
                        />
                      ))}
                      <IconButton
                        size="small"
                        onClick={() => handleEditContacts(u)}
                        disabled={busyAction === u.uid}
                        sx={{
                          color: BRAND_ORANGE,
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          p: 0.5,
                          backgroundColor: 'rgba(240,90,40,0.1)',
                          '&:hover': { backgroundColor: 'rgba(240,90,40,0.2)' },
                        }}
                      >
                        <Edit fontSize="inherit" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ position: 'relative' }}>
                    <Chip
                      label={`${(u.allowedProjectIds || []).length} project${(u.allowedProjectIds || []).length !== 1 ? 's' : ''}`}
                      size="small"
                      sx={{ backgroundColor: 'rgba(240,90,40,0.08)', color: BRAND_TEXT, height: 22 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleEditProjects(u)}
                      disabled={busyAction === u.uid}
                      sx={{
                        color: BRAND_ORANGE,
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        p: 0.5,
                        backgroundColor: 'rgba(240,90,40,0.1)',
                        '&:hover': { backgroundColor: 'rgba(240,90,40,0.2)' },
                      }}
                    >
                      <FolderSpecial fontSize="inherit" />
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 160, wordBreak: 'break-all' }}>{u.uid}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Change password">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleChangePassword(u)}
                          disabled={busyAction === u.uid}
                          sx={{ color: BRAND_ORANGE }}
                        >
                          <Key fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Edit username">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleEditUsername(u)}
                          disabled={busyAction === u.uid}
                          sx={{ color: BRAND_ORANGE }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete user">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(u)}
                          disabled={busyAction === u.uid}
                          sx={{ color: BRAND_ORANGE_DARK }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {companyGroups.map((group) => (
              <Box key={group.contactId ?? group.company} sx={{ border: '1px solid rgba(0,0,0,0.1)', borderRadius: 1, p: 2 }}>
                <Typography variant="h6" sx={{ color: BRAND_ORANGE, mb: 1 }}>
                  {group.company}
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Username(s)</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>UID</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.users.map((u) => (
                      <TableRow key={`${group.company}-${u.uid}`} sx={{ '& > td': { py: 0.5, px: 1 } }}>
                        <TableCell>
                          {u.username}
                          {u.usernames && u.usernames.length > 1 ? (
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                              Also: {u.usernames.filter((n) => n !== u.username).join(', ')}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell sx={{ maxWidth: 160, wordBreak: 'break-all' }}>{u.uid}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Change password">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleChangePassword(u)}
                                disabled={busyAction === u.uid}
                                sx={{ color: BRAND_ORANGE }}
                              >
                                <Key fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Edit username">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleEditUsername(u)}
                                disabled={busyAction === u.uid}
                                sx={{ color: BRAND_ORANGE }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete user">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(u)}
                                disabled={busyAction === u.uid}
                                sx={{ color: BRAND_ORANGE_DARK }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ))}
          </Box>
        )}

        {nextPageToken && !loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              onClick={() => loadUsers(false)}
              disabled={loadingMore}
              variant="outlined"
              sx={{ borderColor: BRAND_ORANGE, color: BRAND_ORANGE }}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Contact selection dialog */}
      <Dialog open={contactDialogOpen} onClose={() => setContactDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Select companies</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {Object.entries(contactsMap)
              .sort((a, b) => (a[1] || '').localeCompare(b[1] || ''))
              .map(([id, name]) => {
              const val = Number(id);
              const checked = contactSelection.includes(val);
              return (
                <FormControlLabel
                  key={id}
                  control={
                    <Checkbox
                      checked={checked}
                      onChange={(e) => {
                        const isOn = e.target.checked;
                        setContactSelection((prev) =>
                          isOn ? Array.from(new Set([...prev, val])) : prev.filter((x) => x !== val)
                        );
                      }}
                    />
                  }
                  label={name || `Contact ${id}`}
                />
              );
            })}
            {Object.keys(contactsMap).length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No contacts loaded. Try refresh.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleContactDialogSave}
            variant="contained"
            disabled={busyAction === (contactDialogUser && contactDialogUser.uid)}
            sx={{ backgroundColor: BRAND_ORANGE, '&:hover': { backgroundColor: BRAND_ORANGE_DARK } }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Project selection dialog */}
      <Dialog open={projectDialogOpen} onClose={() => setProjectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Select projects</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
            <TextField
              label="From"
              type="date"
              size="small"
              value={picklistStart}
              onChange={(e) => setPicklistStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                flex: 1,
                '& label.Mui-focused': { color: BRAND_ORANGE },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#d9d9d9' },
                  '&:hover fieldset': { borderColor: BRAND_ORANGE },
                  '&.Mui-focused fieldset': { borderColor: BRAND_ORANGE_DARK },
                },
              }}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              value={picklistEnd}
              onChange={(e) => setPicklistEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                flex: 1,
                '& label.Mui-focused': { color: BRAND_ORANGE },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#d9d9d9' },
                  '&:hover fieldset': { borderColor: BRAND_ORANGE },
                  '&.Mui-focused fieldset': { borderColor: BRAND_ORANGE_DARK },
                },
              }}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={() => loadPicklist()}
              disabled={loadingPicklist}
              sx={{ borderColor: BRAND_ORANGE, color: BRAND_ORANGE, whiteSpace: 'nowrap', minWidth: 'auto' }}
            >
              Load
            </Button>
          </Box>
          <TextField
            label="Search projects"
            size="small"
            value={projectSearch}
            onChange={(e) => setProjectSearch(e.target.value)}
            fullWidth
            sx={{
              mb: 1,
              '& label.Mui-focused': { color: BRAND_ORANGE },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#d9d9d9' },
                '&:hover fieldset': { borderColor: BRAND_ORANGE },
                '&.Mui-focused fieldset': { borderColor: BRAND_ORANGE_DARK },
              },
            }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 340, overflowY: 'auto' }}>
            {loadingPicklist ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
                <CircularProgress size={18} sx={{ color: BRAND_ORANGE }} />
                <Typography variant="body2">Loading projects...</Typography>
              </Box>
            ) : filteredPicklist.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                No projects found
              </Typography>
            ) : (
              filteredPicklist.map((p) => {
                const checked = projectSelection.includes(p.id);
                return (
                  <FormControlLabel
                    key={p.id}
                    sx={{ m: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={(e) => {
                          const isOn = e.target.checked;
                          setProjectSelection((prev) =>
                            isOn ? Array.from(new Set([...prev, p.id])) : prev.filter((x) => x !== p.id),
                          );
                        }}
                      />
                    }
                    label={<Typography variant="body2">{p.displayname || `Project ${p.id}`}</Typography>}
                  />
                );
              })
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProjectDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleProjectDialogSave}
            variant="contained"
            disabled={busyAction === (projectDialogUser && projectDialogUser.uid)}
            sx={{ backgroundColor: BRAND_ORANGE, '&:hover': { backgroundColor: BRAND_ORANGE_DARK } }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ManageCustomerAccess;

